import { useCallback, useMemo } from 'react';

import { useTranslate } from '@dreryk/react-common';
import { DatePicker } from '@dreryk/react-components';

import { CountryField } from '../../../../common/ContactDetailsForm/CountryField';
import { PhoneNumberField } from '../../../../common/ContactDetailsForm/PhoneNumberField';
import { PostalCodeField } from '../../../../common/ContactDetailsForm/PostalCodeField';
import { FlatField } from '../../../../common/FlatField';
import { HouseField } from '../../../../common/HouseField';
import {
   AuthorizedPersonIdentifierTypeField,
   AuthorizedPersonIdentifierTypeVisitor,
   createAuthorizedPersonTypeVisitor,
} from '../../../../common/PersonForm/AuthorizedPersonIdentifier/AuthorizedPersonIdentifierTypeField';
import { AuthorizedPersonIdentifierValueField } from '../../../../common/PersonForm/AuthorizedPersonIdentifier/AuthorizedPersonIdentifierValueField';
import { NationalIdentificationNumberCountryField } from '../../../../common/PersonForm/AuthorizedPersonIdentifier/NationalIdentificationNumberCountryField';
import { PassportCountryField } from '../../../../common/PersonForm/AuthorizedPersonIdentifier/PassportCountryField';
import { PassportValidToField } from '../../../../common/PersonForm/AuthorizedPersonIdentifier/PassportValidToField';
import { FirstNameField } from '../../../../common/PersonForm/FirstNameField';
import { LastNameField } from '../../../../common/PersonForm/LastNameField';
import { MiddleNameField } from '../../../../common/PersonForm/MiddleNameField';
import { StreetField } from '../../../../common/StreetField';
import { StyledFormSection } from '../../../../common/StyledFormSection';
import { StyledNarrowInput } from '../../../../common/StyledNarrowInput';
import { TownField } from '../../../../common/TownField';
import { patientConsentsAndAuthorizationsTranslationKeys, personalDataTranslationKeys } from '../../../../translations';
import { AuthorizationRangeField } from '../AuthorizationRangeField/AuthorizationRangeField';
import { SectionHeaderRequireSign } from '../SectionHeaderRequireSign';
import { StyledSectionHeader } from '../StyledSectionHeader';

import { MedicalDataAccessAuthorizationFormController } from './MedicalDataAccessAuthorizationModalForm.types';
import { StyledMedicalDataAccessAuthorizationModalForm } from './StyledMedicalDataAccessAuthorizationModalForm';

type MedicalDataAccessAuthorizationModalFormProps = Pick<
   MedicalDataAccessAuthorizationFormController,
   'register' | 'watch' | 'getValue' | 'setValue' | 'setFocus' | 'trigger'
>;

// eslint-disable-next-line max-lines-per-function
const MedicalDataAccessAuthorizationModalForm = (props: MedicalDataAccessAuthorizationModalFormProps): JSX.Element => {
   const { watch, register, setValue, setFocus } = props;
   const translate = useTranslate();

   const { authorizedPersonIdentifierType } = watch(['authorizedPersonIdentifierType']);

   const authorizedPersonIdentifierTypeVisitor = useMemo<AuthorizedPersonIdentifierTypeVisitor>(
      () => createAuthorizedPersonTypeVisitor(authorizedPersonIdentifierType),
      [authorizedPersonIdentifierType]
   );

   const handleAuthorizedPersonIdentifierType = useCallback(() => {
      setValue('authorizedPersonIdentifierValue', null);
      setFocus('authorizedPersonIdentifierValue');
   }, [setValue, setFocus]);

   return (
      <StyledMedicalDataAccessAuthorizationModalForm>
         <StyledSectionHeader>
            {translate(patientConsentsAndAuthorizationsTranslationKeys.authorizations.authorizationScope)}
            <SectionHeaderRequireSign required />
         </StyledSectionHeader>
         <AuthorizationRangeField register={props.register} watch={props.watch} trigger={props.trigger} />

         <StyledSectionHeader>
            {translate(patientConsentsAndAuthorizationsTranslationKeys.authorizations.basicData)}
         </StyledSectionHeader>
         <StyledFormSection testId={'basicData'}>
            <FirstNameField register={props.register} />
            <MiddleNameField register={props.register} />
            <LastNameField register={props.register} />
            <AuthorizedPersonIdentifierTypeField
               name={'authorizedPersonIdentifierType'}
               register={register}
               onSelect={handleAuthorizedPersonIdentifierType}
            />
            <AuthorizedPersonIdentifierValueField
               name={'authorizedPersonIdentifierValue'}
               register={register}
               authorizedPersonIdentifierTypeVisitor={authorizedPersonIdentifierTypeVisitor}
            />
            {authorizedPersonIdentifierTypeVisitor({
               visitPesel: () => null,
               visitPassport: () => (
                  <>
                     <div />
                     <PassportCountryField name={'passportCountry'} register={register} />
                     <PassportValidToField name={'passportValidTo'} register={register} />
                  </>
               ),
               visitNationalIdentificationNumber: () => (
                  <NationalIdentificationNumberCountryField
                     name={'nationalIdentificationNumberCountry'}
                     register={register}
                  />
               ),
               visitBirthDate: () => (
                  <DatePicker
                     testId={'birthDate'}
                     controller={props.register('birthDate', { required: true })}
                     defaultValue={null}
                     dayValidators={'dayAfterToday'}
                     label={personalDataTranslationKeys.birthDate.label}
                     placeholder={personalDataTranslationKeys.birthDate.placeholder}
                     withNavigationArrows
                  />
               ),
            })}
         </StyledFormSection>

         <StyledSectionHeader>
            {translate(patientConsentsAndAuthorizationsTranslationKeys.authorizations.contactData)}
         </StyledSectionHeader>
         <StyledFormSection testId={'contactData'}>
            <CountryField register={props.register} />
            <TownField name={'town'} register={props.register} />
            <PostalCodeField register={props.register} watch={props.watch} />
            <StreetField name={'street'} register={props.register} />
            <StyledNarrowInput>
               <HouseField name={'house'} register={props.register} />
            </StyledNarrowInput>
            <StyledNarrowInput>
               <FlatField name={'flat'} register={props.register} />
            </StyledNarrowInput>
            <PhoneNumberField register={props.register} />
         </StyledFormSection>
      </StyledMedicalDataAccessAuthorizationModalForm>
   );
};

export { MedicalDataAccessAuthorizationModalForm };
